import { WebAPISubsidyResult } from '@/globals/webApiTypes/elements/WebAPISubsidy';
import { WebAPISubsidiesPage } from '@/globals/webApiTypes/pages/WebAPISubsidiesPage';
import { languageCode } from '@/globals/config/localization.config';
import { matchesSelectedFiltersByQueryKeys } from '@/sections/filter/matchesSelectedFilters';
import { SelectedFiltersMap } from '@/sections/filter/getSelectedFilters';
import { subsidyFilterQueryKeys } from './subsidyFilter/subsidyFilterKeys';
import { normalizeStatus } from './subsidyFilter/subsidyFilterKeys';

const collator = new Intl.Collator(languageCode, { sensitivity: 'base' });

type SubsidyFilters = WebAPISubsidiesPage['filters'];

const subsidyResultFieldByQueryKey = {
	subject: 'subjects',
	sector: 'sectors',
	status: 'status',
	schemeType: 'scheme',
} as const satisfies Record<
	keyof typeof subsidyFilterQueryKeys,
	keyof WebAPISubsidyResult
>;

const subsidyOptionsFieldByQueryKey = {
	subject: 'subjects',
	sector: 'sectors',
	status: 'statuses',
	schemeType: 'schemes',
} as const satisfies Record<
	keyof typeof subsidyFilterQueryKeys,
	keyof SubsidyFilters
>;

const subsidyNormalizeByQueryKey = {
	status: normalizeStatus,
} as const;

const matchesSelectedFiltersConfig = {
	filterQueryKeys: subsidyFilterQueryKeys,
	resultFieldByQueryKey: subsidyResultFieldByQueryKey,
	optionsFieldByQueryKey: subsidyOptionsFieldByQueryKey,
	normalizeByQueryKey: subsidyNormalizeByQueryKey,
} as const;

const matchesSelectedFilters = (
	result: WebAPISubsidyResult,
	selectedFilters: SelectedFiltersMap,
	filters: SubsidyFilters,
) =>
	matchesSelectedFiltersByQueryKeys({
		result,
		selectedFilters,
		filters,
		...matchesSelectedFiltersConfig,
	});

export const filterSubsidyResults = (
	results: Array<WebAPISubsidyResult> | undefined,
	selectedFilters: SelectedFiltersMap,
	filters: SubsidyFilters,
) => {
	const filtered = results?.filter((result) =>
		matchesSelectedFilters(result, selectedFilters, filters),
	);

	const selectedSectors = selectedFilters.get(subsidyFilterQueryKeys.sector);
	const hasSelectedSectors = selectedSectors && selectedSectors.size > 0;

	const statusPriority = (status: string | undefined) => {
		const normalized = normalizeStatus(status);
		switch (normalized) {
			case 'open':
				return 0;
			case 'temporarily-closed':
				return 1;
			case 'closed':
				return 2;
			default:
				return 3;
		}
	};

	const isSelectedSector = (sectors: string[] | undefined) => {
		if (!hasSelectedSectors || !sectors) return false;
		return sectors.some((sector) => selectedSectors.has(sector.toLowerCase()));
	};

	return (filtered ?? []).sort((a, b) => {
		const aIsSelected = isSelectedSector(a.sectors);
		const bIsSelected = isSelectedSector(b.sectors);

		// Prioritize results with selected branch first
		if (aIsSelected !== bIsSelected) return aIsSelected ? -1 : 1;

		// Then sort by status priority
		const statusCompare = statusPriority(a.status) - statusPriority(b.status);
		if (statusCompare !== 0) return statusCompare;

		// Finally sort alphabetically
		return collator.compare(a.title ?? '', b.title ?? '');
	});
};

export const hasMatchingSubsidyResults = (
	results: Array<WebAPISubsidyResult> | undefined,
	selectedFilters: SelectedFiltersMap,
	filters: SubsidyFilters,
) =>
	(results ?? []).some((result) =>
		matchesSelectedFilters(result, selectedFilters, filters),
	);
